import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { TOOLTIP_DELAY_DURATION_MS } from '../../constants';
import { clampTooltipPosition } from '../../utils/clamp-tooltip-position';

/** Gap in px between the trigger element and the interactive tooltip */
const INTERACTIVE_TOOLTIP_GAP = 4;
/** Extra invisible bridge area to prevent tooltip from closing when moving cursor between trigger and tooltip */
const BRIDGE_PADDING = 5;
/** Minimum distance the tooltip keeps from every viewport edge. */
const VIEWPORT_PADDING = 8;

interface InteractiveTooltipData {
  content: ReactNode;
  triggerRect: DOMRect;
  alignOffset: number;
}

export interface InteractiveTooltipApi {
  show: (
    content: ReactNode,
    triggerElement: HTMLElement,
    alignOffset?: number,
    onHide?: () => void,
    /**
     * Identity of the logical element the tooltip belongs to (e.g. an assignment
     * or time-off id). When a new show() shares the same hideKey as the current
     * one — i.e. moving the cursor between fragments/segments of the SAME element —
     * the previous onHide is NOT invoked, so the element's hover/lift is preserved.
     * Different keys (a different element) still run the previous onHide.
     */
    hideKey?: string | number
  ) => void;
  hide: () => void;
  hideImmediate: () => void;
  /** Pin the tooltip so it stays visible even when cursor leaves */
  pin: () => void;
  /** Unpin and hide the tooltip */
  unpin: () => void;
  /** Re-read the trigger element's position and update the tooltip accordingly */
  updatePosition: () => void;
  isPinned: boolean;
  isVisible: boolean;
}

interface InteractiveTooltipState {
  data: InteractiveTooltipData | null;
  isVisible: boolean;
}

/**
 * Renders the interactive tooltip content as a portal on document.body.
 * Includes an invisible bridge between trigger and tooltip so the cursor
 * can travel from one to the other without the tooltip closing.
 * Also blocks macOS back/forward swipe gestures while cursor is on the tooltip.
 */
export const InteractiveTooltipPortal = ({
  state,
  onMouseEnter,
  onMouseLeave,
}: {
  state: InteractiveTooltipState;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) => {
  const { data, isVisible } = state;
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });
  const { width: tooltipWidth, height: tooltipHeight } = tooltipSize;

  // Measure tooltip size (for the bridge AND the viewport clamp). Measured at
  // opacity:0 on the first paint, before the delayed fade-in — so the clamped
  // position is applied before the tooltip becomes visible (no jump).
  useEffect(() => {
    if (tooltipRef.current && data) {
      setTooltipSize({
        width: tooltipRef.current.offsetWidth,
        height: tooltipRef.current.offsetHeight,
      });
    }
  }, [data]);

  // Block macOS back/forward swipe gesture on the tooltip.
  // Uses both CSS overscroll-behavior and wheel event preventDefault for full coverage.
  useEffect(() => {
    const el = tooltipRef.current;
    if (!el || !isVisible) return;

    const preventGestureNavigation = (e: WheelEvent) => {
      // Prevent horizontal swipe gestures that trigger browser back/forward
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
      }
    };

    el.addEventListener('wheel', preventGestureNavigation, { passive: false });
    return () => {
      el.removeEventListener('wheel', preventGestureNavigation);
    };
  }, [isVisible]);

  // Clamped placement (flips above the trigger near the bottom, shifts in from the
  // left/right edges). Computed only once the tooltip has been measured; before
  // that we fall back to the naive below-the-trigger position for the first,
  // still-invisible paint.
  const placement = useMemo(() => {
    if (!data) return null;
    const { triggerRect, alignOffset } = data;
    const desiredLeft = triggerRect.left + alignOffset;

    if (tooltipWidth <= 0 || tooltipHeight <= 0) {
      return {
        left: desiredLeft,
        top: triggerRect.bottom + INTERACTIVE_TOOLTIP_GAP,
        flippedAbove: false,
      };
    }

    return clampTooltipPosition({
      triggerRect,
      desiredLeft,
      tooltipWidth,
      tooltipHeight,
      gap: INTERACTIVE_TOOLTIP_GAP,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      padding: VIEWPORT_PADDING,
    });
  }, [data, tooltipWidth, tooltipHeight]);

  const tooltipStyles = useMemo(() => {
    if (!placement) return {};

    return {
      position: 'fixed' as const,
      left: placement.left,
      top: placement.top,
      zIndex: 50,
      transition: 'opacity 150ms ease-out',
      opacity: isVisible ? 1 : 0,
      // Prevent invisible tooltip from intercepting mouse events before it appears.
      // Without this, a fast top-to-bottom cursor sweep triggers mouseEnter on the
      // opacity-0 tooltip div, which cancels the pending hide timeout and blocks
      // interactions on the timeline below (scroll, create-assignment phantom).
      pointerEvents: isVisible ? ('auto' as const) : ('none' as const),
    };
  }, [placement, isVisible]);

  const bridgeStyles = useMemo(() => {
    if (!data || !placement) return {};

    const { triggerRect } = data;
    const bridgeWidth = Math.max(tooltipWidth, 200) + BRIDGE_PADDING * 2;

    // The bridge fills the gap between the trigger and the tooltip so the cursor
    // can travel across without the tooltip closing. When the tooltip flips above
    // the trigger, the bridge sits above the trigger top instead of below it.
    const top = placement.flippedAbove
      ? triggerRect.top - INTERACTIVE_TOOLTIP_GAP - BRIDGE_PADDING
      : triggerRect.bottom - BRIDGE_PADDING;

    return {
      position: 'fixed' as const,
      left: placement.left - BRIDGE_PADDING,
      top,
      width: bridgeWidth,
      height: INTERACTIVE_TOOLTIP_GAP + BRIDGE_PADDING * 2,
      zIndex: 49,
    };
  }, [data, placement, tooltipWidth]);

  if (!data) return null;

  return createPortal(
    <>
      {isVisible && (
        <div style={bridgeStyles} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />
      )}
      <div
        ref={tooltipRef}
        data-testid="planner-tooltip"
        className="z-50 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
        style={{
          ...tooltipStyles,
          overscrollBehaviorX: 'none',
          touchAction: 'pan-y',
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {data.content}
      </div>
    </>,
    document.body
  );
};

/**
 * Hook that manages a single interactive tooltip with hover-through support.
 * Returns the API for showing/hiding + the state and mouse handlers for the portal component.
 */
export const useInteractiveTooltip = (
  isScrollingRef: React.RefObject<boolean>
): {
  api: InteractiveTooltipApi;
  portalProps: {
    state: InteractiveTooltipState;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
} => {
  const [data, setData] = useState<InteractiveTooltipData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMouseInsideRef = useRef(false);
  const isPinnedRef = useRef(false);
  const [isPinned, setIsPinned] = useState(false);
  const onHideRef = useRef<(() => void) | undefined>(undefined);
  const hideKeyRef = useRef<string | number | undefined>(undefined);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const dataRef = useRef<InteractiveTooltipData | null>(null);

  const clearState = useCallback(() => {
    setIsVisible(false);
    setData(null);
    dataRef.current = null;
    isPinnedRef.current = false;
    setIsPinned(false);
    triggerElementRef.current = null;
    onHideRef.current?.();
    onHideRef.current = undefined;
    hideKeyRef.current = undefined;
  }, []);

  const hideImmediate = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    isMouseInsideRef.current = false;
    isPinnedRef.current = false;
    setIsPinned(false);
    clearState();
  }, [clearState]);

  const show = useCallback(
    (
      content: ReactNode,
      triggerElement: HTMLElement,
      alignOffset: number = 0,
      onHide?: () => void,
      hideKey?: string | number
    ) => {
      if (isScrollingRef.current) return;

      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = null;
      }

      const triggerRect = triggerElement.getBoundingClientRect();
      triggerElementRef.current = triggerElement;
      const newData = { content, triggerRect, alignOffset };
      dataRef.current = newData;
      setData(newData);
      // Run the previous onHide when switching to a DIFFERENT logical element.
      // When a hideKey is provided and matches the current one (moving between
      // fragments/segments of the SAME element), skip it so the element's hover
      // (and its lift animation) is preserved. Without a hideKey, fall back to
      // function-identity comparison (previous behavior).
      const isSameElement =
        hideKey !== undefined && hideKeyRef.current !== undefined && hideKey === hideKeyRef.current;
      if (onHideRef.current && onHideRef.current !== onHide && !isSameElement) {
        onHideRef.current();
      }
      onHideRef.current = onHide;
      hideKeyRef.current = hideKey;

      showTimeoutRef.current = setTimeout(() => {
        if (!isScrollingRef.current) {
          setIsVisible(true);
        }
        showTimeoutRef.current = null;
      }, TOOLTIP_DELAY_DURATION_MS);
    },
    []
  );

  const hide = useCallback(() => {
    if (isPinnedRef.current) return;

    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    hideTimeoutRef.current = setTimeout(() => {
      if (!isMouseInsideRef.current && !isPinnedRef.current) {
        clearState();
      }
      hideTimeoutRef.current = null;
    }, 100);
  }, [clearState]);

  const pin = useCallback(() => {
    isPinnedRef.current = true;
    setIsPinned(true);
    // Cancel any pending hide when pinning
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const unpin = useCallback(() => {
    if (!isPinnedRef.current) return;
    isPinnedRef.current = false;
    setIsPinned(false);
    clearState();
  }, [clearState]);

  const handleMouseEnter = useCallback(() => {
    isMouseInsideRef.current = true;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    isMouseInsideRef.current = false;
    if (isPinnedRef.current) return;

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      if (!isPinnedRef.current) {
        clearState();
      }
      hideTimeoutRef.current = null;
    }, 100);
  }, [clearState]);

  // Re-read the trigger element's bounding rect and update tooltip position
  const updatePosition = useCallback(() => {
    if (!triggerElementRef.current || !dataRef.current) return;
    const triggerRect = triggerElementRef.current.getBoundingClientRect();
    setData(prev => (prev ? { ...prev, triggerRect } : null));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // Stable methods reference that does NOT change when isVisible changes.
  // This prevents unnecessary re-renders in consumers that only need the methods.
  const methods = useMemo(
    () => ({ show, hide, hideImmediate, pin, unpin, updatePosition }),
    [show, hide, hideImmediate, pin, unpin, updatePosition]
  );

  const api = useMemo(() => ({ ...methods, isPinned, isVisible }), [methods, isPinned, isVisible]);

  const portalProps = useMemo(
    () => ({
      state: { data, isVisible },
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    }),
    [data, isVisible, handleMouseEnter, handleMouseLeave]
  );

  return { api, portalProps };
};
